import { useCallback, useMemo, useState } from "react";
import { Box, Flex, HStack, Text, VStack, chakra } from "@chakra-ui/react";
import { useReq } from "@larner.dev/use-req";
import dayjs from "dayjs";
import debounce from "lodash.debounce";
import Slider from "rc-slider";
import {
  GenderPreference,
  UserWithMediaAndGroups,
} from "@greenflagdate/shared";
import { Icon } from "../../../components/Icon/Icon";
import { apiReq } from "../../../lib/apiReq";
import {
  refreshUserState,
  store,
  useStoreData,
} from "../../../lib/store/store";
import { ActionType } from "../../../lib/store/storeActions";
import { toaster } from "../../../lib/toaster";
import { ModalPage } from "../../../components/ModalPage";
import { SettingToggle } from "../../../components/SettingToggle/SettingToggle";
import "rc-slider/assets/index.css";

const GENDER_OPTIONS: { value: GenderPreference; label: string }[] = [
  { value: "man", label: "Men" },
  { value: "woman", label: "Women" },
  { value: "everyone", label: "Everyone" },
];

const AGE_MIN = 18;
const AGE_MAX = 70;

function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <Text
      as="div"
      fontSize="10px"
      letterSpacing="0.14em"
      textTransform="uppercase"
      color="fg.muted"
      fontWeight="semibold"
      mb="10px"
    >
      {children}
    </Text>
  );
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <Box
      bg="bg.canvas"
      border="1px solid"
      borderColor="border.subtle"
      borderRadius="sm"
      boxShadow="card"
      overflow="hidden"
    >
      {children}
    </Box>
  );
}

const debouncedSaveAgeRange = debounce(
  async (
    range: number[],
    updateUserReq: ReturnType<typeof useReq<UserWithMediaAndGroups>>[0]
  ) => {
    const response = await updateUserReq.put("/user/attributes", {
      min_age_preference: range[0],
      max_age_preference: range[1],
    });
    if (response.success) {
      store.dispatch({
        type: ActionType.UpdateUser,
        params: { user: response.result },
      });
    } else {
      toaster.create({
        type: "error",
        title: "Couldn't save your age preference.",
      });
    }
  },
  600
);

export function Dating() {
  const storeData = useStoreData();
  const user = storeData.user;

  const [updateUserReq, updateUserReqState] =
    useReq<UserWithMediaAndGroups>(apiReq);

  const [ageRangeOverride, setAgeRangeOverride] = useState<number[] | undefined>();

  const ageRange = useMemo(() => {
    const age = user?.birth_date ? dayjs().diff(user.birth_date, "year") : 30;
    const gap = Math.min(10, age - Math.ceil(age / 2 + 7));
    return (
      ageRangeOverride || [
        user?.min_age_preference || Math.max(21, age - gap),
        user?.max_age_preference || Math.min(100, age + gap),
      ]
    );
  }, [
    ageRangeOverride,
    user?.birth_date,
    user?.max_age_preference,
    user?.min_age_preference,
  ]);

  const saveAgeRange = useCallback(
    (range: number[]) => {
      debouncedSaveAgeRange(range, updateUserReq);
    },
    [updateUserReq]
  );

  const saveGender = useCallback(
    async (value: GenderPreference) => {
      const response = await updateUserReq.put("/user/attributes", {
        gender_preference: value,
      });
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
      } else {
        toaster.create({
          type: "error",
          title: "Couldn't save your dating preference.",
        });
      }
    },
    [updateUserReq]
  );

  const friendsOnly = user?.friends_only ?? false;

  const saveFriendsOnly = useCallback(
    async (value: boolean) => {
      const response = await updateUserReq.put("/user/attributes", {
        friends_only: value,
      });
      if (response.success) {
        store.dispatch({
          type: ActionType.UpdateUser,
          params: { user: response.result },
        });
        // Every past-spark participant's dating/friend category is computed
        // server-side from THIS flag, so the cached pastSparks are stale the
        // instant it flips. Without this, client-side navigation away from
        // here reads the new mode against the old categories.
        await refreshUserState();
      } else {
        toaster.create({
          type: "error",
          title: "Couldn't save your Friends Only setting.",
        });
      }
    },
    [updateUserReq]
  );

  const currentGender = user?.gender_preference;
  const saving = updateUserReqState.loading;
  // Friends Only takes the dating preferences out of play, so everything below
  // it is locked (and dimmed) while it's on. The saved values are left intact
  // — turning it back off restores them rather than making the user re-pick.
  const preferencesDisabled = saving || friendsOnly;

  return (
    <ModalPage
      motionKey="settings-dating-modal"
      title="Dating Preferences"
      fallback="/settings"
      bodyBg="bg.canvas"
    >
    <Flex direction="column" minH="100%" w="100%">
      <Box flex="1" w="100%" maxW="40rem" mx="auto" pt="18px" pb="2rem">
        <Box px="4" mb="2">
          <Text fontSize="13px" color="fg.muted" lineHeight="1.5">
            Tell us who you'd like to meet so we can match you at events.
          </Text>
        </Box>

        <Box px="4" pt="4">
          <Eyebrow>Account Mode</Eyebrow>
          <Card>
            <Flex align="center" gap="14px" p="14px 16px">
              <Flex
                w="36px"
                h="36px"
                borderRadius="full"
                align="center"
                justify="center"
                flexShrink={0}
                bg="bg.friendsSoft"
                color="fg.friends"
              >
                <Icon name="smile" boxSize="16px" />
              </Flex>
              <Box flex={1} minW="0">
                <Text
                  fontSize="14px"
                  fontWeight="semibold"
                  color="fg.default"
                  lineHeight="1.25"
                >
                  Friends Only
                </Text>
                <Text fontSize="12px" color="fg.muted" lineHeight="1.4" mt="2px">
                  Come to events for new friends! Your profile will only show
                  up to match as friends after events you attend.
                </Text>
              </Box>
              <SettingToggle
                checked={friendsOnly}
                onChange={() => saveFriendsOnly(!friendsOnly)}
                disabled={saving}
                ariaLabel="Friends Only"
              />
            </Flex>
          </Card>
        </Box>

        <Box
          px="4"
          pt="6"
          opacity={friendsOnly ? 0.5 : 1}
          transition="opacity 150ms ease"
        >
          <Eyebrow>Who do you want to date?</Eyebrow>
          <VStack align="stretch" gap="2">
            {GENDER_OPTIONS.map((opt) => {
              const on = currentGender?.toLowerCase() === opt.value;
              return (
                <chakra.button
                  key={opt.value}
                  type="button"
                  onClick={() => !preferencesDisabled && saveGender(opt.value)}
                  disabled={preferencesDisabled}
                  display="flex"
                  alignItems="center"
                  gap="3"
                  w="100%"
                  p="13px 14px"
                  border="1px solid"
                  borderColor={on ? "border.brand" : "border.default"}
                  bg={on ? "bg.brandSoft" : "bg.canvas"}
                  borderRadius="pill"
                  cursor={preferencesDisabled ? "not-allowed" : "pointer"}
                  textAlign="left"
                  transition="all 120ms ease"
                  _hover={
                    !preferencesDisabled && !on ? { bg: "#FAFBFB" } : undefined
                  }
                  opacity={saving ? 0.7 : 1}
                >
                  <Flex
                    w="20px"
                    h="20px"
                    borderRadius="full"
                    border="1.5px solid"
                    borderColor={on ? "border.brand" : "border.default"}
                    bg={on ? "bg.brand" : "transparent"}
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    {on && <Icon name="check" boxSize="10px" color="fg.onBrand" />}
                  </Flex>
                  <Text
                    as="span"
                    fontSize="13px"
                    fontWeight={on ? "semibold" : "medium"}
                    color={on ? "fg.onTint" : "fg.default"}
                  >
                    {opt.label}
                  </Text>
                </chakra.button>
              );
            })}
          </VStack>
        </Box>

        <Box
          px="4"
          pt="6"
          opacity={friendsOnly ? 0.5 : 1}
          transition="opacity 150ms ease"
        >
          <Eyebrow>Age Preference</Eyebrow>
          <Card>
            <Box p="18px 20px 14px">
              <HStack justify="space-between" mb="14px">
                <Text fontSize="13px" color="fg.muted" fontWeight="medium">
                  Age range
                </Text>
                <Text
                  fontSize="14px"
                  fontWeight="semibold"
                  color="fg.default"
                >
                  {ageRange[0]} – {ageRange[1]}
                </Text>
              </HStack>
              <Box px="2" pb="2">
                <Slider
                  range
                  min={AGE_MIN}
                  max={AGE_MAX}
                  value={ageRange}
                  // Only Friends Only locks the slider — an in-flight debounced
                  // save must not yank it out from under a drag in progress.
                  disabled={friendsOnly}
                  onChange={(newRange) => {
                    const next = newRange as number[];
                    setAgeRangeOverride(next);
                    saveAgeRange(next);
                  }}
                  styles={{
                    track: { backgroundColor: "var(--gf-bg-brand, #39B87D)", height: 6 },
                    rail: { backgroundColor: "#E3E6EA", height: 6 },
                    handle: {
                      width: 24,
                      height: 24,
                      marginTop: -9,
                      borderColor: "var(--gf-bg-brand, #39B87D)",
                      backgroundColor: "white",
                      opacity: 1,
                      boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
                    },
                  }}
                />
              </Box>
              <HStack justify="space-between" mt="10px">
                <Text fontSize="11px" color="fg.muted">
                  {AGE_MIN}
                </Text>
                <Text fontSize="11px" color="fg.muted">
                  {AGE_MAX}+
                </Text>
              </HStack>
            </Box>
          </Card>
        </Box>

        {updateUserReqState.error && (
          <Box px="4" pt="3">
            <HStack
              gap="2"
              p="10px 12px"
              bg="#FEF5F5"
              border="1px solid"
              borderColor="fg.danger"
              borderRadius="xs"
            >
              <Icon name="x" boxSize="14px" color="fg.danger" />
              <Text fontSize="12px" color="fg.danger">
                {updateUserReqState.error.code ||
                  "Something went wrong saving your changes."}
              </Text>
            </HStack>
          </Box>
        )}
      </Box>
    </Flex>
    </ModalPage>
  );
}
